import { extractApiError, type HttpApiClient } from "@vana/core";
import { type Referral } from "../../domain/entities/referral";
import {
  CreateReferralError,
  GetOriginationUrlError,
  GetReferralCodeError,
  ListReferralsError,
} from "../../domain/errors/errors";
import {
  type CreateReferralParams,
  type ReferralsRepository,
} from "../../domain/repos/referrals-repo";
import {
  type ParameterResponse,
  type ReferralResponse,
  type ReferralsResponse,
  type UserResponse,
} from "../../infrastructure/responses/referrals-response";

// The business-parameter key for the external origination form URL.
const ORIGINATION_URL_PARAMETER_KEY = "origination_form_url";

export class ReferralsRepo implements ReferralsRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listReferrals(requestId: string): Promise<Referral[]> {
    try {
      const response = await this.apiClient.get<ReferralsResponse>(
        `v1/requests/${requestId}/referrals`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListReferralsError(message, apiError);
    }
  }

  async createReferral({
    requestId,
    firstName,
    lastName,
    phone,
    nickname,
  }: CreateReferralParams): Promise<Referral> {
    try {
      // The request interceptor snake_cases the body (firstName -> first_name).
      const response = await this.apiClient.post<
        { firstName: string; lastName?: string; phone: string; nickname?: string },
        ReferralResponse
      >(`v1/requests/${requestId}/referrals`, { firstName, lastName, phone, nickname });
      if (!response.data) throw new CreateReferralError("Empty referral response");
      return response.data;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new CreateReferralError(message, apiError);
    }
  }

  async getOriginationUrl(): Promise<string> {
    try {
      const response = await this.apiClient.get<ParameterResponse>(
        `v1/parameters/${ORIGINATION_URL_PARAMETER_KEY}`,
      );
      return response.data?.valueText ?? "";
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetOriginationUrlError(message, apiError);
    }
  }

  async getReferralCode(userId: string): Promise<string> {
    try {
      const response = await this.apiClient.get<UserResponse>(`v1/users/${userId}`);
      return response.data?.referralCode ?? "";
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetReferralCodeError(message, apiError);
    }
  }
}
